import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import clsx from "clsx";
import { usePostHog } from "@posthog/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import RequestPasswordResetModal from "../../components/auth/RequestPasswordResetModal";
import Button from "../../components/ui/Button/Button";
import InstallAppModal from "../../components/ui/InstallAppModal/InstallAppModal";
import Modal from "../../components/ui/Modal/Modal";
import PageActionBar from "../../components/ui/Page/PageActionBar";
import PageFormSection from "../../components/ui/Page/PageFormSection";
import PageHero from "../../components/ui/Page/PageHero";
import PageShell from "../../components/ui/Page/PageShell";
import SkillSelector from "../../components/ui/SkillSelector/SkillSelector";
import Toast, { type ToastTone } from "../../components/ui/Toast/Toast";
import UsLocationFields from "../../components/ui/UsLocationFields/UsLocationFields";
import { useAuth } from "../../hooks/useAuth";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import { accountApi } from "../../lib/account";
import { captureTeenProfilePhotoUploaded, captureTeenProfileUpdated } from "../../lib/analytics";
import { mediaApi } from "../../lib/media";
import { stripeApi } from "../../lib/stripe";
import { COMPANY_EMAIL, COMPANY_EMAIL_MAILTO } from "../../constants/company";
import { companyName } from "../../constants/constants";
import { buildGuardianSetupHref, requiresGuardianForTeen } from "../../utils/GuardianSetupUtils";
import type {
  StripeIdentityVerificationResponse,
  StripePayoutProfileResponse,
} from "../../types/stripeTypes";
import { apiErrorToMessage } from "../../utils/ApiUtils";
import { hasRole, isBusinessAccount } from "../../utils/AuthUtils";
import { formatDateTime } from "../../utils/DateUtils";

type ToastState = { message: string; tone: ToastTone } | null;
type ProfileFocusTarget = "bio" | "skills";
type PayoutSetupPanelState = {
  statusLabel: string;
  title: string;
  description: string;
  actionLabel: string;
  tone: "success" | "warning" | "info";
};

const isGuardianManagedPayout = (profile: StripePayoutProfileResponse | null) => profile?.payoutOwnerType === "GUARDIAN";
const isReadyGuardianManagedPayout = (profile: StripePayoutProfileResponse | null) =>
  isGuardianManagedPayout(profile) && profile?.onboardingStatus === "READY";

const identityStatusHeading = (verification: StripeIdentityVerificationResponse | null) => {
  switch (verification?.status) {
    case "VERIFIED":
      return "ID verification complete";
    case "PENDING":
      return "Verification in progress";
    case "REQUIRES_INPUT":
      return "Verification needs attention";
    default:
      return "Verify your identity";
  }
};

const identityStatusDescription = (verification: StripeIdentityVerificationResponse | null) => {
  switch (verification?.status) {
    case "VERIFIED":
      return "Your verified badge is active and will appear on your posted gig cards so families can trust who is behind the account.";
    case "PENDING":
      return "Stripe is reviewing your submitted ID. We will update your badge as soon as verification finishes so your account can show as verified.";
    case "REQUIRES_INPUT":
      return verification.lastErrorMessage || "Stripe needs another verification attempt before the badge can be approved.";
    default:
      return "Complete a Stripe ID check to earn a verified badge on the gigs you post and help families feel more confident about who they are hiring.";
  }
};

const TERMS_URL = "https://myteengig.com/terms";
const PRIVACY_URL = "https://myteengig.com/privacy";
const MINOR_GUARDIAN_PAYOUT_REQUIRED_MESSAGE = "Because you are under 18, connect a parent or guardian to your MyTeenGig account.";

const minorStripePayoutBenefits = [
  {
    title: "Secure payments",
    description: "When you are accepted for a paid gig, the poster's funds are held through Stripe and released after the gig is completed.",
  },
  {
    title: "No chasing payments",
    description: "No worrying about partial payments or chasing cash, Venmo, Zelle, or checks.",
  },
  {
    title: "Extra layer of safety",
    description: "Stripe adds another layer of safety for members using MyTeenGig through its ID and bank account verification process.",
  },
] as const;

const SetupCheckIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m5.5 10.25 3 3 6-6.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SetupClockIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 5.5V10l3 2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
);

const SetupMailIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.75 5.5h12.5v9H3.75v-9Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m4.5 6.25 5.5 4 5.5-4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SetupInfoIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" fill="currentColor" />
    <path d="M10 8.75v4.25" stroke="white" strokeWidth="1.7" strokeLinecap="round" />
    <path d="M10 6.75h.01" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const SetupLockIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.75 8.75V7a4.25 4.25 0 0 1 8.5 0v1.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M4.75 8.75h10.5v7H4.75v-7Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const SetupAlertIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 5.25v6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M10 15h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const SetupSecureIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 8V6.75a4 4 0 0 1 8 0V8"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M5 8h10v7.25H5V8Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const SetupShieldIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 3.25 15 5v4.1c0 3.15-1.9 5.95-5 7.4-3.1-1.45-5-4.25-5-7.4V5l5-1.75Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const SetupDollarIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4v12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M12.5 6.25H9.25a2 2 0 0 0-.35 3.97l2.2.39a2 2 0 0 1-.35 3.97H7.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const SetupCardIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.75 5.5h12.5v9H3.75v-9Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M4.5 8.25h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M6.5 12h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const selfManagedStripePayoutBenefits = [
  {
    title: "Secure payments",
    description: "When you are accepted for a paid gig, the gig poster's funds are held through Stripe and released after the gig is completed.",
    Icon: SetupSecureIcon,
  },
  {
    title: "No chasing payments",
    description: "No worrying about partial payments or chasing cash, Venmo, Zelle, or checks.",
    Icon: SetupCheckIcon,
  },
  {
    title: "Extra layer of safety",
    description: "Stripe provides identity and bank account verification where required, adding another layer of security for MyTeenGig members.",
    Icon: SetupShieldIcon,
  },
] as const;

type MinorStripePayoutSetupProps = {
  payoutProfile: StripePayoutProfileResponse | null;
  payoutProfileError: string | null;
  isLoadingPayoutProfile: boolean;
  hasEligiblePayoutGuardians: boolean;
  selectedPayoutGuardianId: string;
  isSavingPayoutGuardian: boolean;
  isSendingGuardianPayoutEmail: boolean;
  onSelectedPayoutGuardianIdChange: (guardianUserId: string) => void;
  onSelectPayoutGuardian: () => void;
  onSendGuardianPayoutEmail: () => void;
  onAddGuardian: () => void;
};

const MinorStripePayoutSetup = ({
  payoutProfile,
  payoutProfileError,
  isLoadingPayoutProfile,
  hasEligiblePayoutGuardians,
  selectedPayoutGuardianId,
  isSavingPayoutGuardian,
  isSendingGuardianPayoutEmail,
  onSelectedPayoutGuardianIdChange,
  onSelectPayoutGuardian,
  onSendGuardianPayoutEmail,
  onAddGuardian,
}: MinorStripePayoutSetupProps) => {
  const selectedGuardian =
    payoutProfile?.eligibleGuardians.find((guardian) => guardian.guardianUserId === selectedPayoutGuardianId)
    ?? payoutProfile?.eligibleGuardians.find((guardian) => guardian.selected)
    ?? null;
  const hasSavedPayoutGuardian = Boolean(payoutProfile?.guardianUserId);
  const hasPayoutGuardianSelectionChange = Boolean(
    selectedPayoutGuardianId && selectedPayoutGuardianId !== payoutProfile?.guardianUserId,
  );
  const setupComplete = isReadyGuardianManagedPayout(payoutProfile);
  const setupDisplayComplete = setupComplete && !hasPayoutGuardianSelectionChange;
  const requiresGuardianSelection = payoutProfile?.requiresGuardianSelection ?? !hasSavedPayoutGuardian;
  const savedGuardianName = payoutProfile?.guardianName;
  const selectedGuardianName = selectedGuardian?.guardianName ?? savedGuardianName ?? "No guardian selected";
  const selectedGuardianEmail = selectedGuardian?.guardianEmail ?? null;
  const guardianEmailSentAt = payoutProfile?.guardianOnboardingEmailSentAt;
  const canSendGuardianPayoutEmail = Boolean(
    isGuardianManagedPayout(payoutProfile)
    && !setupComplete
    && !requiresGuardianSelection
    && !hasPayoutGuardianSelectionChange
    && (payoutProfile?.guardianOnboardingEmailAllowed ?? false),
  );
  const guardianStatusLabel = isLoadingPayoutProfile
    ? "Loading..."
    : hasPayoutGuardianSelectionChange
        ? "Not saved yet"
        : setupDisplayComplete
          ? "Setup complete"
          : requiresGuardianSelection
            ? "Select a guardian"
            : guardianEmailSentAt
              ? `Invite sent ${formatDateTime(guardianEmailSentAt)}`
              : "Invite not sent";
  const guardianStatusClassName = clsx(
    "mt-1 inline-flex items-center gap-1.5 text-sm font-semibold",
    setupDisplayComplete
      ? "text-emerald-700"
      : guardianEmailSentAt && !hasPayoutGuardianSelectionChange
        ? "text-myteengig-primary"
        : "text-amber-700",
  );
  const guardianActionLabel = isSavingPayoutGuardian
    ? "Saving..."
    : hasPayoutGuardianSelectionChange
      ? "Use guardian"
      : "Add guardian";
  const sendLinkLabel = isSendingGuardianPayoutEmail
    ? "Sending..."
    : guardianEmailSentAt
      ? "Resend setup link"
      : "Send Setup Link to Guardian";
  const fullWidthSendLinkLabel = isSendingGuardianPayoutEmail
    ? "Sending setup link..."
    : guardianEmailSentAt
      ? "Resend payout account setup link"
      : "Send Setup Link to Guardian";

  const handleGuardianAction = () => {
    if (hasPayoutGuardianSelectionChange) {
      onSelectPayoutGuardian();
      return;
    }

    onAddGuardian();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-myteengig-border bg-white p-4 text-left shadow-sm sm:p-5">
        <h3 className="text-base font-semibold text-myteengig-text-primary">Guardian setup</h3>
        <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">
          Select a parent or guardian to send a secure link to complete Stripe onboarding.
        </p>

        <div className="mt-4 rounded-xl border border-myteengig-border bg-white p-4">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Selected guardian</p>
              <p className="mt-1 break-words text-sm font-semibold text-myteengig-text-primary">{selectedGuardianName}</p>
              {selectedGuardianEmail ? (
                <p className="mt-1 break-all text-sm text-myteengig-text-muted">{selectedGuardianEmail}</p>
              ) : (
                <p className="mt-1 text-sm text-myteengig-text-muted">
                  Choose an active parent or guardian below.
                </p>
              )}
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
              <p className={guardianStatusClassName}>
                {setupDisplayComplete ? <SetupCheckIcon /> : <SetupClockIcon />}
                {guardianStatusLabel}
              </p>
            </div>

            {!setupDisplayComplete ? (
              <Button
                type="button"
                onClick={onSendGuardianPayoutEmail}
                disabled={
                  isLoadingPayoutProfile ||
                  isSavingPayoutGuardian ||
                  isSendingGuardianPayoutEmail ||
                  !canSendGuardianPayoutEmail
                }
                className="inline-flex w-full items-center justify-center gap-2 md:w-auto"
              >
                <SetupMailIcon />
                {sendLinkLabel}
              </Button>
            ) : (
              <span className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
                <SetupCheckIcon />
                Complete
              </span>
            )}
          </div>

          <div className="mt-4 border-t border-myteengig-border pt-4">
            <p className="text-xs font-semibold text-myteengig-text-primary">Need a different guardian?</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <label htmlFor="settings-minor-payout-guardian" className="sr-only">
                Select parent or guardian
              </label>
              <select
                id="settings-minor-payout-guardian"
                value={selectedPayoutGuardianId}
                onChange={(event) => onSelectedPayoutGuardianIdChange(event.target.value)}
                disabled={isLoadingPayoutProfile || isSavingPayoutGuardian || !hasEligiblePayoutGuardians}
                className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary disabled:cursor-not-allowed disabled:bg-myteengig-background"
              >
                <option value="">
                  {hasEligiblePayoutGuardians ? "Select parent or guardian" : "No active guardians yet"}
                </option>
                {(payoutProfile?.eligibleGuardians ?? []).map((guardian) => (
                  <option key={guardian.guardianUserId} value={guardian.guardianUserId}>
                    {guardian.guardianName} ({guardian.guardianEmail})
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                onClick={handleGuardianAction}
                disabled={isLoadingPayoutProfile || isSavingPayoutGuardian}
                className="w-full sm:min-w-36"
              >
                {guardianActionLabel}
              </Button>
            </div>
          </div>
        </div>

        {payoutProfile?.guardianOnboardingEmailCooldownEndsAt && !payoutProfile.guardianOnboardingEmailAllowed ? (
          <p className="mt-3 text-sm text-myteengig-text-muted">
            Another setup link can be sent after {formatDateTime(payoutProfile.guardianOnboardingEmailCooldownEndsAt)}.
          </p>
        ) : null}

        {payoutProfileError ? (
          <p className="mt-3 text-sm font-medium text-red-600">{payoutProfileError}</p>
        ) : null}

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-myteengig-primary/5 px-3 py-3 text-sm leading-6 text-myteengig-text-muted">
          <span className="mt-1 text-myteengig-primary">
            <SetupInfoIcon />
          </span>
          <p>
            Your guardian will receive instructions to complete Stripe onboarding in their own workspace. This allows you to get paid securely.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-myteengig-border bg-white p-4 text-left shadow-sm sm:p-5">
        <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)]">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f1efff] text-lg font-bold lowercase text-[#635bff]">
            stripe
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-myteengig-text-primary">How Stripe handles your payouts</h3>
            <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">
              Teens are paid through Stripe so MyTeenGig can help make sure you get paid.
            </p>

            <div className="mt-4 divide-y divide-myteengig-border/70">
              {minorStripePayoutBenefits.map((benefit) => (
                <div key={benefit.title} className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[auto_minmax(0,1fr)]">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-emerald-500 text-emerald-600">
                    <SetupCheckIcon />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-myteengig-text-primary">{benefit.title}</p>
                    <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {!setupDisplayComplete ? (
        <Button
          type="button"
          onClick={onSendGuardianPayoutEmail}
          disabled={
            isLoadingPayoutProfile ||
            isSavingPayoutGuardian ||
            isSendingGuardianPayoutEmail ||
            !canSendGuardianPayoutEmail
          }
          className="inline-flex w-full items-center justify-center gap-2 py-3"
        >
          <SetupMailIcon />
          {fullWidthSendLinkLabel}
        </Button>
      ) : null}

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-myteengig-text-muted">
        <SetupLockIcon />
        Payments are released after a gig is marked complete and the holding period ends.
      </p>
    </div>
  );
};

type SelfManagedStripePayoutSetupProps = {
  payoutProfile: StripePayoutProfileResponse | null;
  payoutProfileError: string | null;
  isLoadingPayoutProfile: boolean;
  isStartingPayoutOnboarding: boolean;
  isSavingPayoutOwner: boolean;
  onStartPayoutOnboarding: () => void;
  onSwitchToSelfManagedPayouts: () => void;
};

const SelfManagedStripePayoutSetup = ({
  payoutProfile,
  payoutProfileError,
  isLoadingPayoutProfile,
  isStartingPayoutOnboarding,
  isSavingPayoutOwner,
  onStartPayoutOnboarding,
  onSwitchToSelfManagedPayouts,
}: SelfManagedStripePayoutSetupProps) => {
  const guardianManaged = isGuardianManagedPayout(payoutProfile);
  const ready = !guardianManaged && payoutProfile?.onboardingStatus === "READY";
  const onboardingRequired = !guardianManaged && payoutProfile?.onboardingStatus === "ONBOARDING_REQUIRED";
  const statusTone = ready ? "success" : guardianManaged ? "info" : "warning";
  const statusBadgeLabel = ready ? "READY" : guardianManaged ? "CURRENT OPTION" : "ACTION NEEDED";
  const statusTitle = isLoadingPayoutProfile
    ? "Loading payout account"
    : ready
      ? "Payout account is ready"
      : guardianManaged
        ? "Guardian-managed payout account"
        : onboardingRequired
          ? "Finish payout account setup"
          : "Payout account setup is required";
  const statusDescription = isLoadingPayoutProfile
    ? "Checking your payout account now."
    : ready
      ? "Your payout account is ready to receive payments after paid gigs are completed."
      : guardianManaged
        ? payoutProfile?.guardianName
          ? `Payments currently route through ${payoutProfile.guardianName}. You can switch future payments to your own payout account.`
          : "Payments currently route through a guardian. You can switch future payments to your own payout account."
        : onboardingRequired
          ? "One more step is needed before you can receive payments."
          : "You haven't set up your payout account yet.";
  const primaryActionLabel = guardianManaged
    ? isSavingPayoutOwner
      ? "Switching..."
      : "Use my own payout account"
    : isStartingPayoutOnboarding
      ? "Opening Stripe..."
      : ready
        ? "Review Payout Account"
        : onboardingRequired
          ? "Continue Setup"
          : "Set Up Payout Account";
  const primaryActionDescription = guardianManaged
    ? "Switch to your own payout account to receive payments directly."
    : ready
      ? "Review your payout account details or update bank information directly with Stripe."
      : "Set up your payout account to receive payments directly.";
  const statusIconClassName = clsx(
    "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white",
    statusTone === "success" ? "bg-emerald-600" : "bg-myteengig-primary",
  );
  const statusBadgeClassName = clsx(
    "inline-flex shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
    statusTone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : statusTone === "info"
        ? "border-myteengig-border bg-white text-myteengig-primary"
        : "border-amber-200 bg-amber-50 text-amber-800",
  );

  const handlePrimaryAction = () => {
    if (guardianManaged) {
      onSwitchToSelfManagedPayouts();
      return;
    }

    onStartPayoutOnboarding();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-myteengig-border bg-myteengig-primary/5 p-4 text-left shadow-sm sm:p-5">
        <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
          <span className={statusIconClassName}>
            {ready ? <SetupCheckIcon /> : <SetupAlertIcon />}
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-myteengig-text-primary">{statusTitle}</h3>
            <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{statusDescription}</p>
          </div>
          {!isLoadingPayoutProfile ? (
            <span className={statusBadgeClassName}>{statusBadgeLabel}</span>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-myteengig-border bg-white p-4 text-left shadow-sm sm:p-5">
        <h3 className="text-base font-semibold text-myteengig-text-primary">How your payout account works</h3>
        <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">
          MyTeenGig uses Stripe to securely collect the payout account details needed to send your payments.
        </p>

        <div className="mt-5 divide-y divide-myteengig-border/70">
          {selfManagedStripePayoutBenefits.map(({ title, description, Icon }) => (
            <div key={title} className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[auto_minmax(0,1fr)]">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Icon />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-myteengig-text-primary">{title}</p>
                <p className="mt-1 text-sm leading-6 text-myteengig-text-muted">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-myteengig-border bg-white p-4 text-left shadow-sm sm:p-5">
        <h3 className="text-base font-semibold text-myteengig-text-primary">You manage your payout account</h3>
        <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{primaryActionDescription}</p>

        <div className="mt-4 flex items-start gap-3 rounded-xl bg-myteengig-primary/5 p-4">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-myteengig-primary/30 bg-white text-myteengig-primary">
            <SetupDollarIcon />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-myteengig-text-primary">
              Future payments will be deposited into the bank account on file with Stripe.
            </p>
          </div>
        </div>

        {payoutProfileError ? (
          <p className="mt-3 text-sm font-medium text-red-600">{payoutProfileError}</p>
        ) : null}

        <Button
          type="button"
          onClick={handlePrimaryAction}
          disabled={isLoadingPayoutProfile || isStartingPayoutOnboarding || isSavingPayoutOwner}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 py-3"
        >
          <SetupCardIcon />
          {primaryActionLabel}
        </Button>
      </div>

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-myteengig-text-muted">
        <SetupLockIcon />
        Payments are released after a gig is marked complete and the holding period ends.
      </p>
    </div>
  );
};

const formatUsPhoneDigits = (digits: string) => {
  const limitedDigits = digits.slice(0, 10);
  if (limitedDigits.length <= 3) {
    return limitedDigits.length > 0 ? `(${limitedDigits}` : "";
  }
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  }
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

const formatEditablePhoneNumber = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("+")) {
    const digits = trimmed.slice(1).replace(/\D/g, "");
    if (!digits) {
      return "+";
    }
    if (digits.startsWith("1")) {
      const usDigits = digits.slice(1, 11);
      if (!usDigits) {
        return "+1";
      }
      return `+1 ${formatUsPhoneDigits(usDigits)}`.trimEnd();
    }
    return `+${digits.slice(0, 15)}`;
  }

  return formatUsPhoneDigits(trimmed.replace(/\D/g, ""));
};

const identityStatusValue = (verification: StripeIdentityVerificationResponse | null) => {
  switch (verification?.status) {
    case "VERIFIED":
      return null;
    case "PENDING":
      return "In review";
    case "REQUIRES_INPUT":
      return "Action needed";
    default:
      return "Not started";
  }
};

const buildPayoutSetupPanelState = (
  profile: StripePayoutProfileResponse | null,
  isLoading: boolean,
  teenCanSelfManagePayouts: boolean,
): PayoutSetupPanelState => {
  if (isLoading) {
    return {
      statusLabel: "Checking",
      title: "Checking payout account",
      description: "Checking whether your payout account is ready.",
      actionLabel: "Review Payout Account",
      tone: "info",
    };
  }

  if (profile?.onboardingStatus === "READY" && profile.payoutsEnabled) {
    return {
      statusLabel: "Ready",
      title: "Payout account is ready",
      description: "Payments can be sent to your payout account after a funded gig is completed.",
      actionLabel: "Review Payout Account",
      tone: "success",
    };
  }

  if (!teenCanSelfManagePayouts) {
    if (profile?.requiresGuardianSelection) {
      return {
        statusLabel: "Guardian required",
        title: "Choose the parent/guardian to set up your account",
        description: "A parent or guardian must connect an account so money you earn from gigs can be sent to it.",
        actionLabel: "Set Up Account",
        tone: "warning",
      };
    }

    if (profile?.guardianOnboardingEmailSentAt) {
      return {
        statusLabel: "Invite sent",
        title: "Waiting on guardian",
        description: `Payout account setup instructions were sent${profile.guardianName ? ` to ${profile.guardianName}` : ""}.`,
        actionLabel: "Review Payout Account",
        tone: "info",
      };
    }

    return {
      statusLabel: "Needs setup",
      title: "Guardian setup needed",
      description: "Send a secure setup link so your parent or guardian can create the payout account.",
      actionLabel: "Send Setup Link",
      tone: "warning",
    };
  }

  if (profile?.payoutOwnerType === "GUARDIAN") {
    return {
      statusLabel: "Guardian-managed",
      title: "Guardian-managed payout account",
      description: "You can keep guardian-managed payments or switch future paid gigs to your own payout account.",
      actionLabel: "Review Payout Account",
      tone: "info",
    };
  }

  return {
    statusLabel: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Needs one more step" : "Needs setup",
    title: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Continue payout account setup" : "Set Up Payout Account",
    description: "Set up the account where payments will be sent after you complete paid gigs.",
    actionLabel: profile?.onboardingStatus === "ONBOARDING_REQUIRED" ? "Continue Setup" : "Set Up Payout Account",
    tone: "warning",
  };
};

const parseProfileFocus = (value: string | null): ProfileFocusTarget | null => {
  if (value === "bio" || value === "skills") {
    return value;
  }
  return null;
};

const SettingsPage = () => {
  const posthog = usePostHog();
  const { user, status, logout, initialize } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isInstalled, isInstallHelpOpen, setIsInstallHelpOpen, handleInstallClick } = useInstallPrompt();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isSavingAccountProfile, setIsSavingAccountProfile] = useState(false);
  const [isUploadingProfilePhoto, setIsUploadingProfilePhoto] = useState(false);
  const [isRemovingProfilePhoto, setIsRemovingProfilePhoto] = useState(false);
  const [isRequestingEmailChange, setIsRequestingEmailChange] = useState(false);
  const [isSavingPhoneNumber, setIsSavingPhoneNumber] = useState(false);
  const [isResendingPhoneCode, setIsResendingPhoneCode] = useState(false);
  const [isVerifyingPhoneCode, setIsVerifyingPhoneCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [isEmailChangeModalOpen, setIsEmailChangeModalOpen] = useState(false);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [guardianPayoutSetupError, setGuardianPayoutSetupError] = useState<string | null>(null);
  const [isPayoutSetupModalOpen, setIsPayoutSetupModalOpen] = useState(false);
  const [isLoadingPayoutProfile, setIsLoadingPayoutProfile] = useState(false);
  const [isStartingPayoutOnboarding, setIsStartingPayoutOnboarding] = useState(false);
  const [isSavingPayoutGuardian, setIsSavingPayoutGuardian] = useState(false);
  const [isSavingPayoutOwner, setIsSavingPayoutOwner] = useState(false);
  const [isSendingGuardianPayoutEmail, setIsSendingGuardianPayoutEmail] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [payoutProfile, setPayoutProfile] = useState<StripePayoutProfileResponse | null>(null);
  const [payoutProfileError, setPayoutProfileError] = useState<string | null>(null);
  const [selectedPayoutGuardianId, setSelectedPayoutGuardianId] = useState("");
  const [identityVerification, setIdentityVerification] = useState<StripeIdentityVerificationResponse | null>(null);
  const [identityVerificationError, setIdentityVerificationError] = useState<string | null>(null);
  const [isLoadingIdentityVerification, setIsLoadingIdentityVerification] = useState(false);
  const [isStartingIdentityVerification, setIsStartingIdentityVerification] = useState(false);
  const [isCompletingInstallReminder, setIsCompletingInstallReminder] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountPassword, setDeleteAccountPassword] = useState("");
  const [deleteAccountConfirmation, setDeleteAccountConfirmation] = useState("");
  const [deleteAccountReason, setDeleteAccountReason] = useState("");
  const [deleteAccountError, setDeleteAccountError] = useState<string | null>(null);
  const [activeSettingsTab, setActiveSettingsTab] = useState("profile");
  const [openSettingsSectionId, setOpenSettingsSectionId] = useState<string | null>("profile");
  const profilePhotoInputRef = useRef<HTMLInputElement | null>(null);
  const profileSectionRef = useRef<HTMLElement | null>(null);
  const bioFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const skillsSectionRef = useRef<HTMLDivElement | null>(null);
  const contactSectionRef = useRef<HTMLElement | null>(null);
  const payoutSectionRef = useRef<HTMLElement | null>(null);
  const identitySectionRef = useRef<HTMLElement | null>(null);
  const homeScreenSectionRef = useRef<HTMLDivElement | null>(null);
  const logoutSectionRef = useRef<HTMLElement | null>(null);
  const completedInstallReminderForUserIdRef = useRef<string | null>(null);
  const isTeen = hasRole(user?.roles, "TEEN");
  const isAdult = hasRole(user?.roles, "ADULT");
  const isBusiness = isBusinessAccount(user?.accountType);
  const pendingPhoneNumber = user?.pendingPhoneNumber ?? null;
  const currentPhoneNumber = user?.phoneNumber ?? null;
  const verificationTargetPhoneNumber = pendingPhoneNumber ?? (!user?.phoneVerified ? currentPhoneNumber : null);
  const teenCanSelfManagePayouts = Boolean(isTeen && !requiresGuardianForTeen(user?.dateOfBirth));
  const hasEligiblePayoutGuardians = (payoutProfile?.eligibleGuardians?.length ?? 0) > 0;

  const completeInstallAppReminder = useCallback(async () => {
    if (!user?.id || completedInstallReminderForUserIdRef.current === user.id) {
      return;
    }

    setIsCompletingInstallReminder(true);
    try {
      await accountApi.completeInstallAppReminder();
      completedInstallReminderForUserIdRef.current = user.id;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["notifications-summary"] }),
      ]);
    } catch {
      completedInstallReminderForUserIdRef.current = null;
    } finally {
      setIsCompletingInstallReminder(false);
    }
  }, [queryClient, user?.id]);

  const onAddToHomeScreen = async () => {
    void completeInstallAppReminder();
    await handleInstallClick();
  };

  const settingsTabs = [
    { id: "profile", label: "Profile", ref: profileSectionRef },
    { id: "contact", label: "Contact", ref: contactSectionRef },
    ...(isTeen ? [{ id: "gig-payouts", label: "Payout Account", ref: payoutSectionRef }] : []),
    ...(isAdult ? [{ id: "identity", label: "Identity", ref: identitySectionRef }] : []),
    { id: "logout", label: "Log out", ref: logoutSectionRef },
  ];

  const scrollToSettingsSection = (tabId: string, ref: RefObject<HTMLElement | null>) => {
    setActiveSettingsTab(tabId);
    setOpenSettingsSectionId(tabId);
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const toggleSettingsSection = (tabId: string) => {
    setActiveSettingsTab(tabId);
    setOpenSettingsSectionId((currentTabId) => (currentTabId === tabId ? null : tabId));
  };

  const getProfileFocusTarget = (focus: ProfileFocusTarget) => (
    focus === "bio" ? bioFieldRef.current : skillsSectionRef.current
  );

  const scrollToProfileFocusTarget = (focus: ProfileFocusTarget) => {
    const target = getProfileFocusTarget(focus);
    if (!target) {
      return false;
    }

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (focus === "bio") {
        bioFieldRef.current?.focus({ preventScroll: true });
      }
    });
    return true;
  };

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setCity(user?.city ?? "");
    setState(user?.state ?? "");
    setZip(user?.zip ?? "");
    setBusinessName(user?.businessName ?? "");
    setBusinessDescription(user?.businessDescription ?? "");
    setBusinessWebsite(user?.businessWebsite ?? "");
    setEmail(user?.pendingEmail ?? user?.email ?? "");
    setBio(user?.bio ?? "");
    setSkills(user?.skills ?? []);
    setPhoneNumber(formatEditablePhoneNumber(user?.pendingPhoneNumber ?? user?.phoneNumber ?? ""));
    setVerificationCode("");
  }, [
    user?.firstName,
    user?.lastName,
    user?.city,
    user?.state,
    user?.zip,
    user?.businessName,
    user?.businessDescription,
    user?.businessWebsite,
    user?.bio,
    user?.skills,
    user?.pendingPhoneNumber,
    user?.phoneNumber,
  ]);

  useEffect(() => {
    if (!user?.id || !isInstalled) {
      return;
    }

    void completeInstallAppReminder();
  }, [completeInstallAppReminder, isInstalled, user?.id]);

  const loadPayoutProfile = async (options?: { showErrorToast?: boolean }) => {
    if (!isTeen || status !== "authenticated") {
      setPayoutProfile(null);
      setPayoutProfileError(null);
      return;
    }

    setIsLoadingPayoutProfile(true);
    setPayoutProfileError(null);
    try {
      const profile = await stripeApi.getTeenPayoutProfile();
      setPayoutProfile(profile);
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to load payout onboarding.");
      setPayoutProfile(null);
      setPayoutProfileError(message);
      if (options?.showErrorToast) {
        setToast({ tone: "error", message });
      }
    } finally {
      setIsLoadingPayoutProfile(false);
    }
  };

  const loadIdentityVerification = async (options?: { showErrorToast?: boolean }) => {
    if (!isAdult || status !== "authenticated") {
      setIdentityVerification(null);
      setIdentityVerificationError(null);
      return;
    }

    setIsLoadingIdentityVerification(true);
    setIdentityVerificationError(null);
    try {
      const response = await stripeApi.getAdultIdentityVerification();
      setIdentityVerification(response);
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to load identity verification.");
      setIdentityVerification(null);
      setIdentityVerificationError(message);
      if (options?.showErrorToast) {
        setToast({ tone: "error", message });
      }
    } finally {
      setIsLoadingIdentityVerification(false);
    }
  };

  useEffect(() => {
    void loadPayoutProfile();
  }, [isTeen, status, user?.id]);

  useEffect(() => {
    void loadIdentityVerification();
  }, [isAdult, status, user?.id]);

  useEffect(() => {
    const selectedGuardian = payoutProfile?.eligibleGuardians.find((guardian) => guardian.selected);
    setSelectedPayoutGuardianId(selectedGuardian?.guardianUserId ?? "");
  }, [payoutProfile]);

  useEffect(() => {
    const section = searchParams.get("section");
    const focus = parseProfileFocus(searchParams.get("focus"));
    if (!section) {
      return;
    }

    if (section === "home-screen" && homeScreenSectionRef.current) {
      requestAnimationFrame(() => {
        homeScreenSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("section");
      setSearchParams(nextParams, { replace: true });
      return;
    }

    const targetTab = settingsTabs.find((tab) => tab.id === section);
    if (!targetTab?.ref.current) {
      return;
    }
    if (section === "profile" && focus && !getProfileFocusTarget(focus)) {
      return;
    }

    requestAnimationFrame(() => {
      scrollToSettingsSection(targetTab.id, targetTab.ref);
      if (section === "profile" && focus) {
        scrollToProfileFocusTarget(focus);
      }
      if (section === "gig-payouts") {
        setIsPayoutSetupModalOpen(true);
      }
    });

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("section");
    nextParams.delete("focus");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, settingsTabs, status, user?.id]);

  useEffect(() => {
    if (!openSettingsSectionId) {
      return;
    }

    if (settingsTabs.some((tab) => tab.id === openSettingsSectionId)) {
      return;
    }

    setOpenSettingsSectionId(settingsTabs[0]?.id ?? null);
  }, [openSettingsSectionId, settingsTabs]);

  useEffect(() => {
    if (!isTeen) {
      return;
    }

    const payoutState = searchParams.get("stripePayout");
    if (!payoutState) {
      return;
    }

    const handleStripePayoutReturn = async () => {
      setActiveSettingsTab("gig-payouts");
      setOpenSettingsSectionId("gig-payouts");
      setIsPayoutSetupModalOpen(true);
      await loadPayoutProfile({ showErrorToast: payoutState === "return" });
      if (payoutState === "return") {
        setToast({
          tone: "success",
          message: "Returned from Stripe. Refreshing your payout account status now.",
        });
      } else if (payoutState === "refresh") {
        setToast({
          tone: "info",
          message: "Stripe asked for one more onboarding step. Continue the setup when you are ready.",
        });
      }
    };

    void handleStripePayoutReturn();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stripePayout");
    setSearchParams(nextParams, { replace: true });
  }, [isTeen, searchParams, setSearchParams, status, user?.id]);

  useEffect(() => {
    if (!isAdult) {
      return;
    }

    const identityState = searchParams.get("stripeIdentity");
    if (!identityState) {
      return;
    }

    const handleStripeIdentityReturn = async () => {
      await loadIdentityVerification({ showErrorToast: identityState === "return" });
      await initialize();
      setToast({
        tone: "success",
        message: "Returned from Stripe. Refreshing your identity verification status now.",
      });
    };

    void handleStripeIdentityReturn();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("stripeIdentity");
    setSearchParams(nextParams, { replace: true });
  }, [initialize, isAdult, searchParams, setSearchParams, status, user?.id]);

  const onLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    setErrorMessage(null);
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      if (error instanceof Error && error.message.trim().length > 0) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Failed to log out. Please try again.");
      }
    } finally {
      setIsLoggingOut(false);
    }
  };

  const closeDeleteAccountModal = () => {
    if (isDeletingAccount) {
      return;
    }
    setIsDeleteAccountModalOpen(false);
    setDeleteAccountPassword("");
    setDeleteAccountConfirmation("");
    setDeleteAccountReason("");
    setDeleteAccountError(null);
  };

  const onDeleteAccount = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isDeletingAccount) {
      return;
    }
    if (!deleteAccountPassword.trim()) {
      setDeleteAccountError("Enter your password to delete your account.");
      return;
    }
    if (deleteAccountConfirmation !== "DELETE") {
      setDeleteAccountError("Type DELETE to confirm account deletion.");
      return;
    }

    setIsDeletingAccount(true);
    setDeleteAccountError(null);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.deleteAccount({
        password: deleteAccountPassword,
        confirmationText: deleteAccountConfirmation,
        reason: deleteAccountReason.trim() || undefined,
      });
      posthog?.reset();
      window.location.assign("/login");
    } catch (error) {
      setDeleteAccountError(apiErrorToMessage(error, "Unable to delete account."));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const onSaveAccountProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSavingAccountProfile) {
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMessage("Please enter your first and last name.");
      return;
    }

    if (isBusiness && !businessName.trim()) {
      setErrorMessage("Please enter your business name.");
      return;
    }

    if (!hasResolvedProfileLocation) {
      setErrorMessage("Please enter a valid ZIP code.");
      return;
    }

    if (!/^\d{5}(?:-\d{4})?$/.test(zip.trim())) {
      setErrorMessage("Please enter a valid ZIP code.");
      return;
    }

    setIsSavingAccountProfile(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip: zip.trim(),
        businessName: isBusiness ? businessName.trim() || null : null,
        businessDescription: isBusiness ? businessDescription.trim() || null : null,
        businessWebsite: isBusiness ? businessWebsite.trim() || null : null,
        bio: isTeen ? bio.trim() || null : null,
        skills: isTeen ? skills : [],
      });
      await initialize();
      if (isTeen) {
        captureTeenProfileUpdated(posthog, {
          has_bio: bio.trim().length > 0,
          skills_count: skills.length,
          has_profile_photo: Boolean(user?.profilePhoto),
        });
      }
      setToast({ tone: "success", message: "Profile details updated." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to update profile details."));
    } finally {
      setIsSavingAccountProfile(false);
    }
  };

  const onSelectProfilePhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isUploadingProfilePhoto) {
      return;
    }

    setIsUploadingProfilePhoto(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await mediaApi.uploadFile(file, "PROFILE_PHOTO");
      await initialize();
      if (isTeen) {
        captureTeenProfilePhotoUploaded(posthog, { source: "settings" });
      }
      setToast({ tone: "success", message: "Profile photo updated." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to upload profile photo."));
    } finally {
      setIsUploadingProfilePhoto(false);
    }
  };

  const onSavePhoneNumber = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!phoneNumber.trim() || isSavingPhoneNumber) {
      return;
    }

    setIsSavingPhoneNumber(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.updatePhoneNumber({ phoneNumber: phoneNumber.trim() });
      await initialize();
      setVerificationCode("");
      setToast({ tone: "success", message: "Phone number update is pending verification. A code has been sent." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to save phone number."));
    } finally {
      setIsSavingPhoneNumber(false);
    }
  };

  const onResendPhoneCode = async () => {
    if (isResendingPhoneCode) {
      return;
    }

    setIsResendingPhoneCode(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.resendPhoneVerificationCode();
      setToast({ tone: "success", message: "A new verification code has been sent." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to resend phone verification code."));
    } finally {
      setIsResendingPhoneCode(false);
    }
  };

  const onVerifyPhoneCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!verificationCode.trim() || isVerifyingPhoneCode) {
      return;
    }

    setIsVerifyingPhoneCode(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.verifyPhoneNumber({ code: verificationCode.trim() });
      await initialize();
      setVerificationCode("");
      setToast({ tone: "success", message: "Phone number verified." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to verify phone number."));
    } finally {
      setIsVerifyingPhoneCode(false);
    }
  };

  const onRemoveProfilePhoto = async () => {
    if (!user?.profilePhoto || isRemovingProfilePhoto) {
      return;
    }

    setIsRemovingProfilePhoto(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await mediaApi.removeProfilePhoto();
      await initialize();
      setToast({ tone: "success", message: "Profile photo removed." });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to remove profile photo."));
    } finally {
      setIsRemovingProfilePhoto(false);
    }
  };

  const onStartPayoutOnboarding = async () => {
    if (!isTeen || isStartingPayoutOnboarding || isGuardianManagedPayout(payoutProfile)) {
      return;
    }

    setIsStartingPayoutOnboarding(true);
    setErrorMessage(null);
    setToast(null);
    try {
      const response = await stripeApi.createTeenPayoutOnboardingLink();
      setPayoutProfile(response.profile);
      window.location.assign(response.url);
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to start payout onboarding.");
      if (message.includes(MINOR_GUARDIAN_PAYOUT_REQUIRED_MESSAGE)) {
        setGuardianPayoutSetupError(message);
        setPayoutProfileError(null);
        return;
      }
      setPayoutProfileError(message);
      setErrorMessage(message);
    } finally {
      setIsStartingPayoutOnboarding(false);
    }
  };

  const goToGuardianSetup = () => {
    setGuardianPayoutSetupError(null);
    navigate(buildGuardianSetupHref("/settings?section=gig-payouts"));
  };

  const onStartIdentityVerification = async () => {
    if (!isAdult || isStartingIdentityVerification || identityVerification?.status === "VERIFIED") {
      return;
    }

    setIsStartingIdentityVerification(true);
    setIdentityVerificationError(null);
    setToast(null);
    try {
      const response = await stripeApi.createAdultIdentityVerificationSession();
      setIdentityVerification({
        status: response.status,
        verifiedAt: response.verifiedAt,
        lastErrorMessage: response.lastErrorMessage,
      });
      window.location.assign(response.verificationUrl);
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to start identity verification.");
      setIdentityVerificationError(message);
      setToast({ tone: "error", message });
    } finally {
      setIsStartingIdentityVerification(false);
    }
  };

  const onSendGuardianPayoutEmail = async () => {
    if (!isTeen || isSendingGuardianPayoutEmail || !isGuardianManagedPayout(payoutProfile)) {
      return;
    }

    setIsSendingGuardianPayoutEmail(true);
    setPayoutProfileError(null);
    setErrorMessage(null);
    setToast(null);
    try {
      const profile = await stripeApi.sendTeenPayoutGuardianNotification();
      setPayoutProfile(profile);
      setToast({
        tone: "success",
        message: profile.guardianName
          ? `Sent payout account setup instructions to ${profile.guardianName}.`
          : "Sent payout account setup instructions to the selected guardian.",
      });
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to email the selected guardian about payout account setup.");
      setPayoutProfileError(message);
      setErrorMessage(message);
    } finally {
      setIsSendingGuardianPayoutEmail(false);
    }
  };

  const onSelectPayoutGuardian = async () => {
    if (!isTeen || isSavingPayoutGuardian || !selectedPayoutGuardianId) {
      return;
    }

    setIsSavingPayoutGuardian(true);
    setPayoutProfileError(null);
    setErrorMessage(null);
    setToast(null);
    try {
      const profile = await stripeApi.selectTeenPayoutGuardian({
        guardianUserId: selectedPayoutGuardianId,
      });
      setPayoutProfile(profile);
      setToast({ tone: "success", message: "Payout guardian updated." });
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to update the payout guardian.");
      setPayoutProfileError(message);
      setErrorMessage(message);
    } finally {
      setIsSavingPayoutGuardian(false);
    }
  };

  const onSwitchToSelfManagedPayouts = async () => {
    if (!isTeen || !teenCanSelfManagePayouts || isSavingPayoutOwner || !isGuardianManagedPayout(payoutProfile)) {
      return;
    }

    setIsSavingPayoutOwner(true);
    setPayoutProfileError(null);
    setErrorMessage(null);
    setToast(null);
    try {
      const profile = await stripeApi.updateTeenPayoutOwner({
        payoutOwnerType: "TEEN",
      });
      setPayoutProfile(profile);
      setToast({ tone: "success", message: "Payouts are now set to be managed by your own Stripe account." });
    } catch (error) {
      const message = apiErrorToMessage(error, "Unable to switch payout ownership.");
      setPayoutProfileError(message);
      setErrorMessage(message);
    } finally {
      setIsSavingPayoutOwner(false);
    }
  };

  const onUpdateEmailAddress = async () => {
    if (isRequestingEmailChange) {
      return;
    }

    if (!email.trim()) {
      setErrorMessage("Please enter an email address.");
      return;
    }

    setIsRequestingEmailChange(true);
    setErrorMessage(null);
    setToast(null);
    try {
      await accountApi.requestEmailChange({ email: email.trim() });
      setIsEmailChangeModalOpen(false);
      await initialize();
      setToast({
        tone: "success",
        message: `Verification sent to ${email.trim()}. Your login email will update after you confirm it.`,
      });
    } catch (error) {
      setErrorMessage(apiErrorToMessage(error, "Failed to start email update."));
    } finally {
      setIsRequestingEmailChange(false);
    }
  };

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : "";
  const initials = fullName
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const emailChanged = (user?.pendingEmail ?? user?.email ?? "") !== email.trim();
  const currentEmail = user?.email ?? "";
  const nextEmail = email.trim();
  const profileChanged =
    (user?.firstName ?? "") !== firstName.trim() ||
    (user?.lastName ?? "") !== lastName.trim() ||
    (user?.city ?? "") !== city.trim() ||
    (user?.state ?? "") !== state.trim().toUpperCase() ||
    (user?.zip ?? "") !== zip.trim() ||
    (isBusiness && (user?.businessName ?? "") !== businessName.trim()) ||
    (isBusiness && (user?.businessDescription ?? "") !== businessDescription.trim()) ||
    (isBusiness && (user?.businessWebsite ?? "") !== businessWebsite.trim()) ||
    (isTeen && (user?.bio ?? "") !== bio.trim()) ||
    (isTeen && JSON.stringify(user?.skills ?? []) !== JSON.stringify(skills));
  const hasResolvedProfileLocation = city.trim().length > 0 && state.trim().length === 2 && zip.trim().length > 0;
  const payoutSetupPanelState = buildPayoutSetupPanelState(payoutProfile, isLoadingPayoutProfile, teenCanSelfManagePayouts);
  const payoutSetupToneClassName = clsx(
    "inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
    payoutSetupPanelState.tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : payoutSetupPanelState.tone === "info"
        ? "border-sky-200 bg-sky-50 text-sky-800"
        : "border-amber-200 bg-amber-50 text-amber-800",
  );

  return (
    <PageShell width="6xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <RequestPasswordResetModal
        isOpen={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        defaultEmail={user?.email ?? ""}
        lockEmail
      />
      <Modal
        isOpen={guardianPayoutSetupError !== null}
        onClose={() => setGuardianPayoutSetupError(null)}
        title="Connect a parent or guardian"
        panelClassName="max-w-xl"
      >
        <div className="space-y-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium leading-6 text-amber-900">
              {guardianPayoutSetupError}
            </p>
          </div>
          <p className="text-sm leading-6 text-myteengig-text-muted">
            After the parent or guardian is connected, open Set Up Payout Account and choose who should manage the payout account.
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setGuardianPayoutSetupError(null)}>
              Close
            </Button>
            <Button type="button" onClick={goToGuardianSetup}>
              Go to Guardian Setup
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isPayoutSetupModalOpen}
        onClose={() => {
          if (!isStartingPayoutOnboarding && !isSavingPayoutGuardian && !isSendingGuardianPayoutEmail && !isSavingPayoutOwner) {
            setIsPayoutSetupModalOpen(false);
          }
        }}
        title="Set Up Payout Account"
        panelClassName="max-w-3xl"
      >
        {isTeen ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4">
              <span className={payoutSetupToneClassName}>{payoutSetupPanelState.statusLabel}</span>
              <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{payoutSetupPanelState.title}</p>
              <p className="mt-2 text-sm leading-6 text-myteengig-text-muted">{payoutSetupPanelState.description}</p>
            </div>
            {!teenCanSelfManagePayouts ? (
              <MinorStripePayoutSetup
                payoutProfile={payoutProfile}
                payoutProfileError={payoutProfileError}
                isLoadingPayoutProfile={isLoadingPayoutProfile}
                hasEligiblePayoutGuardians={hasEligiblePayoutGuardians}
                selectedPayoutGuardianId={selectedPayoutGuardianId}
                isSavingPayoutGuardian={isSavingPayoutGuardian}
                isSendingGuardianPayoutEmail={isSendingGuardianPayoutEmail}
                onSelectedPayoutGuardianIdChange={setSelectedPayoutGuardianId}
                onSelectPayoutGuardian={() => void onSelectPayoutGuardian()}
                onSendGuardianPayoutEmail={() => void onSendGuardianPayoutEmail()}
                onAddGuardian={goToGuardianSetup}
              />
            ) : (
              <SelfManagedStripePayoutSetup
                payoutProfile={payoutProfile}
                payoutProfileError={payoutProfileError}
                isLoadingPayoutProfile={isLoadingPayoutProfile}
                isStartingPayoutOnboarding={isStartingPayoutOnboarding}
                isSavingPayoutOwner={isSavingPayoutOwner}
                onStartPayoutOnboarding={() => void onStartPayoutOnboarding()}
                onSwitchToSelfManagedPayouts={() => void onSwitchToSelfManagedPayouts()}
              />
            )}
          </div>
        ) : null}
      </Modal>
      <Modal
        isOpen={isEmailChangeModalOpen}
        onClose={() => {
          if (!isRequestingEmailChange) {
            setIsEmailChangeModalOpen(false);
          }
        }}
        title="Confirm email update"
        panelClassName="max-w-xl"
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            You are updating the email on your account. After you confirm, you will need to verify the new email address and then log back in using that new email.
          </p>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Current email</p>
            <p className="mt-1 break-all text-sm font-semibold text-myteengig-text-primary">{currentEmail || "N/A"}</p>
          </div>
          <div className="rounded-2xl border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.2),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">New email</p>
            <p className="mt-1 break-all text-sm font-semibold text-myteengig-text-primary">{nextEmail || "N/A"}</p>
          </div>
          <p className="text-sm font-medium text-myteengig-text-primary">
            By continuing, you understand that:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-myteengig-text-muted">
            <li>Your account email will not change until the new address is verified.</li>
            <li>You will be logged out when that verification link is completed.</li>
            <li>You will need to log back in with the new email and your current password.</li>
          </ul>
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isRequestingEmailChange}
              onClick={() => setIsEmailChangeModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" disabled={isRequestingEmailChange} onClick={() => void onUpdateEmailAddress()}>
              {isRequestingEmailChange ? "Sending..." : "Yes, update email"}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isDeleteAccountModalOpen}
        onClose={closeDeleteAccountModal}
        title="Delete account"
        panelClassName="max-w-xl"
      >
        <form onSubmit={(event) => void onDeleteAccount(event)} className="space-y-4">
          <p className="text-sm leading-6 text-myteengig-text-muted">
            This closes your account, removes your personal profile details, disconnects active guardian links where allowed, and logs you out. Historical gig, review, payment, and safety records are retained.
          </p>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium leading-6 text-amber-900">
              Account deletion is blocked while you have active gigs, open issues, published listings, or unsettled payments.
            </p>
          </div>
          <div>
            <label htmlFor="delete-account-reason" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              Reason for leaving (optional)
            </label>
            <textarea
              id="delete-account-reason"
              value={deleteAccountReason}
              onChange={(event) => setDeleteAccountReason(event.target.value)}
              maxLength={500}
              rows={3}
              disabled={isDeletingAccount}
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-red-500 disabled:cursor-not-allowed disabled:bg-myteengig-background"
              placeholder="Tell us why you're leaving, if you'd like."
            />
            <p className="mt-1 text-xs text-myteengig-text-muted">Up to 500 characters. MyTeenGig admins can review your feedback after deletion.</p>
          </div>
          <div>
            <label htmlFor="delete-account-password" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              Password
            </label>
            <input
              id="delete-account-password"
              type="password"
              autoComplete="current-password"
              value={deleteAccountPassword}
              onChange={(event) => setDeleteAccountPassword(event.target.value)}
              disabled={isDeletingAccount}
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-red-500 disabled:cursor-not-allowed disabled:bg-myteengig-background"
            />
          </div>
          <div>
            <label htmlFor="delete-account-confirmation" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
              Type DELETE
            </label>
            <input
              id="delete-account-confirmation"
              type="text"
              value={deleteAccountConfirmation}
              onChange={(event) => setDeleteAccountConfirmation(event.target.value)}
              disabled={isDeletingAccount}
              className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-red-500 disabled:cursor-not-allowed disabled:bg-myteengig-background"
            />
          </div>
          {deleteAccountError ? (
            <p className="text-sm font-medium text-red-700">{deleteAccountError}</p>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isDeletingAccount}
              onClick={closeDeleteAccountModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isDeletingAccount || !deleteAccountPassword.trim() || deleteAccountConfirmation !== "DELETE"}
              className="border-red-700 bg-red-700 text-white hover:bg-red-800 disabled:opacity-60"
            >
              {isDeletingAccount ? "Deleting..." : "Delete account"}
            </Button>
          </div>
        </form>
      </Modal>
      <PageHero
        eyebrow="Account Settings"
        title="Manage your MyTeenGig profile"
        description="Review your account details and keep the profile information on your gig applications current."
        className="bg-gradient-to-br from-white via-myteengig-background to-white"
        footer={
          <div className="flex flex-wrap items-center justify-center gap-2">
            {settingsTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => scrollToSettingsSection(tab.id, tab.ref)}
                className={clsx(
                  "rounded-full border px-4 py-2 text-sm font-semibold transition",
                  activeSettingsTab === tab.id
                    ? "border-myteengig-primary bg-myteengig-primary text-white"
                    : "border-myteengig-border bg-white text-myteengig-text-muted hover:border-myteengig-primary hover:text-myteengig-text-primary",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      />

      <div className="mt-8 grid gap-6 xl:grid-cols-[1.05fr_0.95fr] xl:items-start">
        <div className="space-y-6">
        <section ref={profileSectionRef} className="scroll-mt-28">
        <PageFormSection
          eyebrow="Account"
          title="Profile details"
          description="Update the standard profile details attached to your account."
          centered
          collapsible
          isOpen={openSettingsSectionId === "profile"}
          onToggle={() => toggleSettingsSection("profile")}
          bodyId="settings-profile-section-body"
        >

          {status === "loading" || status === "idle" ? (
            <p className="mt-6 text-sm text-myteengig-text-muted">Loading your account information...</p>
          ) : null}

          {status === "authenticated" && user ? (
            <>
              <div className="mt-5 flex flex-col items-center gap-4 rounded-2xl border border-myteengig-border bg-myteengig-background/70 px-4 py-5 text-center">
                <div className="inline-flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-myteengig-border bg-white text-2xl font-semibold text-myteengig-text-primary shadow-sm">
                  {user.profilePhoto ? (
                    <img src={user.profilePhoto.url} alt={`${fullName || "User"} profile`} className="h-full w-full object-cover" />
                  ) : (
                    initials || "MT"
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-myteengig-text-primary">Profile photo</p>
                  <p className="mt-1 text-xs text-myteengig-text-muted">Add a photo so your account feels more personal across the site.</p>
                </div>
                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(event) => void onSelectProfilePhoto(event)}
                />
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploadingProfilePhoto}
                    onClick={() => profilePhotoInputRef.current?.click()}
                  >
                    {isUploadingProfilePhoto ? "Uploading..." : user.profilePhoto ? "Replace photo" : "Upload photo"}
                  </Button>
                  {user.profilePhoto ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isRemovingProfilePhoto}
                      onClick={() => void onRemoveProfilePhoto()}
                    >
                      {isRemovingProfilePhoto ? "Removing..." : "Remove photo"}
                    </Button>
                  ) : null}
                </div>
              </div>

              <form id="settings-account-profile-form" onSubmit={(event) => void onSaveAccountProfile(event)} className="mt-5 space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                    <label htmlFor="settings-first-name" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                      First name
                    </label>
                    <input
                      id="settings-first-name"
                      type="text"
                      autoComplete="given-name"
                      maxLength={100}
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                      placeholder="Alex"
                    />
                  </div>

                  <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                    <label htmlFor="settings-last-name" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                      Last name
                    </label>
                    <input
                      id="settings-last-name"
                      type="text"
                      autoComplete="family-name"
                      maxLength={100}
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                      placeholder="Taylor"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                  <UsLocationFields
                    idPrefix="settings"
                    city={city}
                    state={state}
                    zip={zip}
                    rounded="xl"
                    onCityChange={setCity}
                    onStateChange={setState}
                    onZipChange={setZip}
                  />
                </div>

                {isBusiness ? (
                  <>
                    <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                      <label htmlFor="settings-business-name" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                        Business name
                      </label>
                      <input
                        id="settings-business-name"
                        type="text"
                        maxLength={160}
                        value={businessName}
                        onChange={(event) => setBusinessName(event.target.value)}
                        className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        placeholder="Harbor Scoops"
                      />
                    </div>

                    <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                      <label htmlFor="settings-business-website" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                        Business website
                      </label>
                      <input
                        id="settings-business-website"
                        type="url"
                        maxLength={255}
                        value={businessWebsite}
                        onChange={(event) => setBusinessWebsite(event.target.value)}
                        className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                      <label htmlFor="settings-business-description" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                        Business description
                      </label>
                      <textarea
                        id="settings-business-description"
                        rows={5}
                        maxLength={2000}
                        value={businessDescription}
                        onChange={(event) => setBusinessDescription(event.target.value)}
                        className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        placeholder="Describe the business, the work environment, and what applicants should know."
                      />
                      <p className="mt-2 text-xs text-myteengig-text-muted">{businessDescription.length}/2000 characters</p>
                    </div>
                  </>
                ) : null}

                {isTeen ? (
                  <>
                    <div className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                      <label htmlFor="settings-teen-bio" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                        About you
                      </label>
                      <textarea
                        ref={bioFieldRef}
                        id="settings-teen-bio"
                        rows={5}
                        maxLength={500}
                        value={bio}
                        onChange={(event) => setBio(event.target.value)}
                        className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                        placeholder="Share what kind of work you enjoy, when you are available, and what makes you dependable."
                      />
                      <p className="mt-2 text-xs text-myteengig-text-muted">{bio.length}/500 characters</p>
                    </div>

                    <div ref={skillsSectionRef} className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                      <SkillSelector
                        idPrefix="settings-teen-skills"
                        label="Skills"
                        helperText="These tags help match you to gigs and help posters review your application."
                        selectedSkills={skills}
                        onChange={setSkills}
                      />
                    </div>
                  </>
                ) : null}

                <PageActionBar align="between" className="border-t border-myteengig-border pt-1">
                  <Button type="submit" disabled={!profileChanged || isSavingAccountProfile} className="w-full sm:w-auto">
                    {isSavingAccountProfile ? "Saving..." : "Save profile details"}
                  </Button>
                </PageActionBar>

              </form>
            </>
          ) : null}

          {status === "anonymous" ? (
            <p className="mt-6 text-sm text-myteengig-text-muted">No authenticated user found.</p>
          ) : null}

          {errorMessage ? <p className="mt-4 text-sm font-medium text-red-600">{errorMessage}</p> : null}
        </PageFormSection>
        </section>

          <section ref={contactSectionRef} className="scroll-mt-28">
          <PageFormSection
            eyebrow="Contact Info"
            title="Email and phone number"
            description="Keep your contact info current. Email and phone updates each need their own verification flow."
            centered
            collapsible
            isOpen={openSettingsSectionId === "contact"}
            onToggle={() => toggleSettingsSection("contact")}
            bodyId="settings-contact-section-body"
          >
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.2),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-4">
                <label htmlFor="settings-email" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                  Email address
                </label>
                <input
                  id="settings-email"
                  type="email"
                  autoComplete="email"
                  maxLength={320}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="you@example.com"
                />
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  {user?.pendingEmail
                    ? `Pending verification: ${user.pendingEmail}`
                    : "Changing your email sends a verification link to the new address before it becomes your login email."}
                </p>
              </div>

              <form id="settings-phone-number-form" onSubmit={(event) => void onSavePhoneNumber(event)} className="rounded-2xl border border-myteengig-border bg-myteengig-background/60 p-4">
                <label htmlFor="settings-phone-number" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                  Phone number
                </label>
                <input
                  id="settings-phone-number"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={20}
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(formatEditablePhoneNumber(event.target.value))}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="(555) 123-4567"
                />
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  US 10-digit numbers are accepted, or enter an E.164 number such as +15551234567.
                </p>
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  {pendingPhoneNumber
                    ? `Pending verification: ${pendingPhoneNumber}`
                    : currentPhoneNumber && user?.phoneVerified
                      ? `Current verified phone: ${currentPhoneNumber}`
                      : "Phone verification is optional and can be managed from settings whenever you choose."}
                </p>
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  Verified phone numbers are only updated after you confirm the SMS code.
                </p>
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  By entering your phone number and clicking Update phone number, you agree to receive transactional SMS from MyTeenGig for one-time verification codes and important account notifications. Message and data rates may apply. Message frequency varies. Reply HELP for help and STOP to cancel.
                </p>
                <p className="mt-2 text-xs text-myteengig-text-muted">
                  View our{" "}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-myteengig-primary underline underline-offset-2"
                  >
                    Terms and Conditions
                  </a>
                  {" "}and{" "}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-myteengig-primary underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                  . Support:{" "}
                  <a href={COMPANY_EMAIL_MAILTO} className="font-medium text-myteengig-primary underline underline-offset-2">
                    {COMPANY_EMAIL}
                  </a>
                  .
                </p>
              </form>

            </div>

            <PageActionBar align="between" className="mt-5">
              <Button type="submit" form="settings-phone-number-form" disabled={!phoneNumber.trim() || isSavingPhoneNumber} className="w-full sm:w-auto">
                {isSavingPhoneNumber ? "Saving..." : "Update phone number"}
              </Button>
              <Button
                type="button"
                onClick={() => setIsEmailChangeModalOpen(true)}
                disabled={!emailChanged || isRequestingEmailChange}
                className="w-full sm:w-auto"
              >
                Update email address
              </Button>
            </PageActionBar>

            {verificationTargetPhoneNumber ? (
              <form onSubmit={(event) => void onVerifyPhoneCode(event)} className="mt-6 space-y-4 rounded-2xl border border-dashed border-myteengig-border p-4">
                <div>
                  <label htmlFor="settings-phone-code" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                    Verification code
                  </label>
                  <input
                    id="settings-phone-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm tracking-[0.25em] text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                    placeholder="123456"
                  />
                  <p className="mt-2 text-xs text-myteengig-text-muted">
                    Enter the 6-digit code sent to {verificationTargetPhoneNumber}. You can resend it if needed.
                  </p>
                </div>

                <PageActionBar align="between">
                  <Button type="button" variant="outline" disabled={isResendingPhoneCode} onClick={() => void onResendPhoneCode()} className="w-full sm:w-auto">
                    {isResendingPhoneCode ? "Resending..." : "Resend code"}
                  </Button>
                  <Button type="submit" disabled={verificationCode.trim().length !== 6 || isVerifyingPhoneCode} className="w-full sm:w-auto">
                    {isVerifyingPhoneCode ? "Verifying..." : "Verify phone number"}
                  </Button>
                </PageActionBar>
              </form>
            ) : null}
          </PageFormSection>
          </section>

        </div>

        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setIsPasswordResetModalOpen(true)}
            className="block w-full rounded-[1.75rem] border border-myteengig-border bg-white p-6 text-center shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Password</p>
            <h2 className="mt-2 text-xl font-semibold text-myteengig-text-primary">Reset your password</h2>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Send yourself a secure email link to choose a new password.
            </p>
          </button>

          {isTeen ? (
            <section ref={payoutSectionRef} className="scroll-mt-28">
              <button
                type="button"
                onClick={() => setIsPayoutSetupModalOpen(true)}
                disabled={isLoadingPayoutProfile}
                className="block w-full rounded-[1.75rem] border border-myteengig-border bg-white p-6 text-center shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-sm"
              >
                <span className={payoutSetupToneClassName}>{payoutSetupPanelState.statusLabel}</span>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Payments</p>
                <h2 className="mt-2 text-xl font-semibold text-myteengig-text-primary">Set Up Payout Account</h2>
                <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-myteengig-text-muted">{payoutSetupPanelState.description}</p>
                <span className="mt-5 inline-flex items-center justify-center rounded-md bg-myteengig-primary px-4 py-2 text-sm font-semibold text-white">
                  {payoutSetupPanelState.actionLabel}
                </span>
                {payoutProfileError ? (
                  <p className="mt-3 text-sm font-medium text-red-600">{payoutProfileError}</p>
                ) : null}
              </button>
            </section>
          ) : null}

          {isAdult ? (
            <section ref={identitySectionRef} className="scroll-mt-28">
            <PageFormSection
              eyebrow="Identity"
              title={identityStatusHeading(identityVerification)}
              description={identityStatusDescription(identityVerification)}
              centered
              collapsible
              isOpen={openSettingsSectionId === "identity"}
              onToggle={() => toggleSettingsSection("identity")}
              bodyId="settings-identity-section-body"
            >
              <div className="mt-5 rounded-2xl bg-myteengig-background px-4 py-4 text-center">
                <div className="grid gap-3 text-center sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
                    <div className="mt-2 flex items-center justify-center gap-3">
                      {isLoadingIdentityVerification || identityStatusValue(identityVerification) ? (
                        <p className="text-sm font-medium text-myteengig-text-primary">
                          {isLoadingIdentityVerification ? "Loading..." : identityStatusValue(identityVerification)}
                        </p>
                      ) : null}
                      {(identityVerification?.status ?? user?.identityVerificationStatus) === "VERIFIED" ? (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                          ID VERIFIED
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Verified at</p>
                    <p className="mt-1 text-sm font-medium text-myteengig-text-primary">
                      {identityVerification?.verifiedAt ? formatDateTime(identityVerification.verifiedAt) : "Not verified yet"}
                    </p>
                  </div>
                </div>
                {identityVerification?.lastErrorMessage ? (
                  <p className="mt-4 text-sm text-amber-700">{identityVerification.lastErrorMessage}</p>
                ) : null}
              </div>

              <div className="mt-4 rounded-2xl border border-myteengig-border bg-white px-4 py-4 text-center">
                <p className="text-sm text-myteengig-text-muted">
                  We ask adults to verify their identity so teens and guardians can feel more confident that the person posting a gig is real.
                  This helps build trust, reduces fake accounts, and makes the platform safer before a gig is accepted or funded.
                </p>
              </div>

              {identityVerificationError ? (
                <p className="mt-4 text-sm font-medium text-red-600">{identityVerificationError}</p>
              ) : null}

              <PageActionBar align="between" className="mt-5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void loadIdentityVerification({ showErrorToast: true })}
                  disabled={isLoadingIdentityVerification}
                  className="w-full sm:w-auto"
                >
                  {isLoadingIdentityVerification ? "Refreshing..." : "Refresh status"}
                </Button>
                <Button
                  type="button"
                  onClick={() => void onStartIdentityVerification()}
                  disabled={isStartingIdentityVerification || identityVerification?.status === "VERIFIED"}
                  className="w-full sm:w-auto"
                >
                  {isStartingIdentityVerification
                    ? "Opening Stripe..."
                    : identityVerification?.status === "VERIFIED"
                      ? "Verified"
                      : identityVerification?.status === "PENDING"
                        ? "Continue verification"
                        : identityVerification?.status === "REQUIRES_INPUT"
                          ? "Retry verification"
                          : "Verify my ID"}
                </Button>
              </PageActionBar>
            </PageFormSection>
            </section>
          ) : null}

          {isTeen ? (
            <Link
              to="/teen/experience-report"
              className="block rounded-[1.75rem] border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(191,239,255,0.45),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-6 text-center shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Experience Report</p>
              <div className="mt-2 flex items-center justify-center gap-4">
                <div className="max-w-xl">
                  <h2 className="text-xl font-semibold text-myteengig-text-primary">Open your teen report</h2>
                  <p className="mt-2 text-sm text-myteengig-text-muted">
                    View your completed gig summary, ratings, review highlights, and downloadable PDF in one place.
                  </p>
                </div>
                <span className="text-lg font-semibold text-myteengig-primary" aria-hidden="true">
                  →
                </span>
              </div>
            </Link>
          ) : null}

          <div ref={homeScreenSectionRef} className="rounded-[1.75rem] border border-myteengig-border bg-white p-6 text-center shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Home Screen</p>
            <h2 className="mt-2 text-xl font-semibold text-myteengig-text-primary">
              {isInstalled ? "Already installed" : `Add ${companyName}`}
            </h2>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Save MyTeenGig on your phone for quicker access to messages, gigs, and account actions.
            </p>
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => void onAddToHomeScreen()}
                disabled={isInstalled}
                className="w-full sm:w-auto"
              >
                {isInstalled ? "Added to Home Screen" : isCompletingInstallReminder ? "Saving..." : "Add to Home Screen"}
              </Button>
            </div>
          </div>

          <section ref={logoutSectionRef} className="scroll-mt-28">
            <div className="rounded-[1.75rem] border border-myteengig-border bg-white p-6 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-primary">Account Access</p>
              <h2 className="mt-2 text-xl font-semibold text-myteengig-text-primary">Log out</h2>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                End this session and return to the login screen. Your account and profile details stay unchanged.
              </p>
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onLogout}
                  disabled={isLoggingOut}
                  className="w-full disabled:opacity-60 sm:w-auto"
                >
                  {isLoggingOut ? "Logging out..." : "Log out"}
                </Button>
              </div>
            </div>
          </section>

          <div className="rounded-[1.75rem] border border-rose-200 bg-rose-50/70 p-6 text-center shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700">Account deletion</p>
            <h2 className="mt-2 text-xl font-semibold text-myteengig-text-primary">Delete your account</h2>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              Account deletion removes profile details but keeps records needed for safety, payments, and dispute history.
            </p>
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                onClick={() => {
                  setDeleteAccountError(null);
                  setIsDeleteAccountModalOpen(true);
                }}
                disabled={isDeletingAccount}
                className="w-full border-red-700 bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 sm:w-auto"
              >
                Delete account
              </Button>
            </div>
          </div>

        </div>
      </div>
      <InstallAppModal isOpen={isInstallHelpOpen} onClose={() => setIsInstallHelpOpen(false)} />
    </PageShell>
  );
};

export default SettingsPage;
